import { WarningOutlined } from "@mui/icons-material";
import { Box, Button, Link, Stack, Typography } from "@mui/material";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import AppButton from "../../components/app-button/AppButton";
import AppDialogDelete from "../../components/app-dialog-delete/AppDialogDelete";
import AppTable from "../../components/app-table/AppTable";
import Iconify from "../../components/iconify/Iconify";
import {
  getUsersByStore,
  userAction,
  userDelete,
} from "../../reduxStore/slices/userSlice";
import { useNotify } from "../../utils/snackbarUtils";
import UserDialog from "./UserDialog";

function Users(props) {
  const { showSuccess, handleApiError, showError } = useNotify();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector((state) => state.auth);
  const { list: userList, loading: isLoading } = useSelector(
    (state) => state.user,
  );
  const storeId = user?.storeId;
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState({});

  const tableHead = [
    { id: "name", label: "Name", align: "left" },
    { id: "mobileNo", label: "Mobile No.", align: "left" },
    { id: "role", label: "Role", align: "left" },
    { id: "status", label: "Status", align: "left" },
    { id: "createdAt", label: "Created At", align: "left" },
    { id: "actions", label: "Actions", align: "center", width: "120px" },
  ];

  const tableRows = userList.map((user) => ({
    name: `${user.first_name || ""} ${user.last_name || ""}`.trim(),
    ...user,
  }));

  const getAllUsers = async () => {
    try {
      await dispatch(getUsersByStore({ storeId })).unwrap();
    } catch (err) {
      handleApiError(err);
    }
  };

  useEffect(() => {
    if (storeId) {
      getAllUsers();
    }
  }, [storeId]);

  const handleAddUser = () => {
    setIsAddDialogOpen(true);
  };

  const handleDeleteClose = () => {
    setIsDeleteDialogOpen(false);
  };

  const toggleStatus = async (event, row) => {
    const obj = {
      store_id: storeId,
      uid: row.uid,
      id: row.id,
      record_status: event.target.value === "A" ? "D" : "A",
      token_version: 1,
    };
    try {
      const res = await dispatch(userAction(obj)).unwrap();
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const handleDeleteConfirm = async () => {
    setIsDeleteDialogOpen(false);
    const obj = {
      store_id: storeId,
      uid: selectedRecord.uid,
      id: selectedRecord.id,
      record_status: "R",
      token_version: 1,
    };
    try {
      const res = await dispatch(userDelete(obj)).unwrap();
      showSuccess(res.message);
    } catch (err) {
      handleApiError(err);
    }
  };

  const editActionClick = (event, row) => {
    setSelectedRecord(row);
    setIsAddDialogOpen(true);
  };

  const viewActionClick = () => {
    navigate("/app/users/view/1");
  };

  const deleteActionClick = async (event, row) => {
    setIsDeleteDialogOpen(true);
    setSelectedRecord(row);
  };

  return (
    <Box
      sx={{
        margin: "25px 60px",
      }}
    >
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        mb={3}
      >
        <Typography variant="h5" gutterBottom>
          All Users
        </Typography>
        <Link style={{ textDecoration: "none" }}>
          <AppButton
            onClick={handleAddUser}
            variant="contained"
            startIcon={<Iconify icon="eva:plus-fill" />}
          >
            New User
          </AppButton>
        </Link>
      </Stack>
      <Box>
        <AppTable
          tableHead={tableHead}
          tableRows={tableRows}
          listType="userTables"
          searchPlaceholder="Search By Name"
          toggleSwitchStatus={toggleStatus}
          editActionClick={editActionClick}
          viewActionClick={viewActionClick}
          deleteActionClick={deleteActionClick}
          searchField={"name"}
        />
      </Box>

      {isAddDialogOpen && (
        <UserDialog
          isAddDialogOpen={isAddDialogOpen}
          setIsAddDialogOpen={setIsAddDialogOpen}
          uid={selectedRecord?.uid}
        />
      )}

      {isDeleteDialogOpen && (
        <AppDialogDelete
          isOpen={isDeleteDialogOpen}
          id={"delete_user_dialog"}
          title={<>Delete User</>}
          footer={
            <>
              <Button
                autoFocus
                onClick={handleDeleteConfirm}
                className="confirm-delete-btn"
              >
                Delete
              </Button>
              <Button
                onClick={handleDeleteClose}
                variant="outlined"
                className="cancel-delete-btn"
              >
                Cancel
              </Button>
            </>
          }
        >
          <Typography
            style={{
              fontSize: 14,
              color: "#000",
              display: "flex",
              alignItems: "center",
              gap: 8,
            }}
          >
            <WarningOutlined style={{ fontSize: 20, color: "#FF0000" }} />
            This action cannot be undone. Once you delete this user, they will
            be permanently removed from the system and will not be able to
            login.
          </Typography>
        </AppDialogDelete>
      )}
    </Box>
  );
}

export default Users;
